import { fileAPI } from './api';

// List folders at root level or in a specific path
export const listFolders = async (prefix = '') => {
  try {
    const response = await fileAPI.list(prefix);
    const folders = (response.objects || [])
      .filter(obj => obj.isFolder)
      .map(obj => obj.fullKey);

    console.log('Folders:', folders);
    return folders;
  } catch (error) {
    console.error('Error listing folders:', error);
    throw error;
  }
};

// List objects in a specific folder
export const listObjects = async (folderPath = '') => {
  try {
    const response = await fileAPI.list(folderPath);
    const objects = response.objects || [];

    console.log('Objects in folder:', objects);
    return objects;
  } catch (error) {
    console.error('Error listing objects:', error);
    throw error;
  }
};

// Delete an object
export const deleteObject = async (objectKey) => {
  try {
    const result = await fileAPI.deleteFile(objectKey);
    console.log('File deleted successfully:', objectKey);
    return result;
  } catch (error) {
    console.error('Error deleting object:', error);
    throw error;
  }
};

// Get object details
export const getObjectDetails = async (objectKey) => {
  try {
    const response = await fileAPI.list(objectKey);
    const objects = response.objects || [];
    const object = objects.find(obj => !obj.isFolder);

    if (object) {
      return object;
    }

    throw new Error('Object not found');
  } catch (error) {
    console.error('Error getting object details:', error);
    throw error;
  }
};

// Create a folder
export const createFolder = async (folderPath) => {
  try {
    const normalizedPath = folderPath.endsWith('/') ? folderPath : `${folderPath}/`;

    // Create folder by uploading an empty file via the backend
    const emptyBlob = new Blob([''], { type: 'application/x-directory' });
    const emptyFile = new File([emptyBlob], '.folder', { type: 'application/x-directory' });

    await fileAPI.upload(emptyFile, normalizedPath);
    console.log('Folder created successfully:', normalizedPath);
    return { success: true, folderPath: normalizedPath };
  } catch (error) {
    console.error('Error creating folder:', error);
    throw error;
  }
};

// Delete a folder and all its contents
export const deleteFolder = async (folderPath) => {
  try {
    const normalizedPath = folderPath.endsWith('/') ? folderPath : `${folderPath}/`;
    const result = await fileAPI.deleteFolder(normalizedPath);
    console.log('Folder deleted successfully:', normalizedPath);
    return result;
  } catch (error) {
    console.error('Error deleting folder:', error);
    throw error;
  }
};

// Upload multiple files (bulk upload)
export const uploadFiles = async (files, folderPath = '', onProgress = null) => {
  try {
    const results = [];
    const totalFiles = files.length;

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const result = await uploadFile(file, folderPath);
      results.push(result);

      if (onProgress) {
        onProgress({
          completed: i + 1,
          total: totalFiles,
          percentage: Math.round(((i + 1) / totalFiles) * 100),
          currentFile: file.name
        });
      }
    }

    console.log('Bulk upload completed:', results.length, 'files');
    return { success: true, files: results };
  } catch (error) {
    console.error('Error in bulk upload:', error);
    throw error;
  }
};

// Upload a file
export const uploadFile = async (file, folderPath = '') => {
  try {
    const result = await fileAPI.upload(file, folderPath);
    console.log('File uploaded successfully:', result.key);
    return { success: true, key: result.key, url: result.url };
  } catch (error) {
    console.error('Error uploading file:', error);
    throw error;
  }
};

// Generate a shareable/download link for a file
export const generateShareableLink = async (objectKey, expiresIn = 604800) => {
  try {
    const url = fileAPI.getDownloadUrl(objectKey);
    console.log('Generated shareable link:', url);
    return { success: true, url, expiresIn };
  } catch (error) {
    console.error('Error generating shareable link:', error);
    throw error;
  }
};

// Generate shareable links for all files in a folder
export const generateFolderShareableLinks = async (folderPath, expiresIn = 604800) => {
  try {
    const objects = await listObjects(folderPath);
    const fileObjects = objects.filter(obj => !obj.isFolder);

    const shareableLinks = fileObjects.map(obj => ({
      fileName: obj.key,
      fullKey: obj.fullKey,
      url: fileAPI.getDownloadUrl(obj.fullKey),
      size: obj.size,
      lastModified: obj.lastModified
    }));

    return { success: true, files: shareableLinks, expiresIn };
  } catch (error) {
    console.error('Error generating folder shareable links:', error);
    throw error;
  }
};

export default {
  listFolders,
  listObjects,
  deleteObject,
  deleteFolder,
  getObjectDetails,
  createFolder,
  uploadFile,
  uploadFiles,
  generateShareableLink,
  generateFolderShareableLinks
};
